import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TaskDropdown, TaskDrop, TaskFilterDiv, Data, GroupDiv, DropText, Options, SVGDiv } from '../accounting/styles';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { Dropdown, DropdownItem } from '../dropdown';
import {
  TASK_LIST_TYPE,
  SHOW_COMPLETED_TASK_BY_DATE,
  SHOW_COMPLETED_TASK_FILTER_NAME,
  SHOW_TASK_GROUPING_TYPE,
  SHOW_TASK_GROUPING_NAME,
  SHOW_TASK_BY_ORDER,
  SHOW_TASK_ORDER_NAME,
  TAB_BUTTON_TYPE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  CHIP_TYPE,
  CHIP_SIZE,
  CHIP_ICON_POSITION,
  ICON_SIZE,
  PREFERENCE_WRITE_DEBOUNCE_MS
} from '../../global/constants';
import TaskFilterCustomDropdown from '../taskFilterDropdown';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { batchUpdatePreferences } from '../../helpers/firebaseHelper';
import { defaultDisplayFilters } from '../../mobx/common';
import { NewTaskFilterInterface } from '../../interfaces/TaskInterface';
import Tabs from '../tabs';
import { viewFilterTaskOption } from '../../global/row';
import Modal from '../modals/modal';
import { useMobileDevice } from '../../global/useMobile';
import IconButton from '../button/iconButton';
import Chip from '../chipComponent';
import SwitchInput from '../input/switchInput';

interface DisplayFilterDropdownProps {
  className?: string;
  displayButtonSize?: 'small' | 'large';
  isModal?: boolean;
  isCreateView?: boolean;
}

const DisplayFilterDropdown: React.FC<DisplayFilterDropdownProps> = ({ displayButtonSize = 'large', isCreateView }) => {
  const [isOrderDropdownOpen, setIsOrderDropdownOpen] = useState(false);
  const [isOpenGroupMenu, setIsOpenGroupMenu] = useState(false);
  const [isOpenCompletedTaskDropdown, setIsOpenCompletedTaskDropdown] = useState(false);

  //Mobx store variables
  const { viewStore, taskStore } = rootStore;
  const { currentView, createViewInput } = viewStore;
  const { isTaskFilterOpen } = taskStore;

  //Other variables
  const displayFilters = createViewInput?.id
    ? createViewInput?.displayFilters
    : currentView?.displayFilters || defaultDisplayFilters;
  const isMobile = useMobileDevice();

  // Debounce Firebase preference writes: local MobX state updates immediately for instant UI
  // feedback, while rapid-fire toggles are coalesced into a single Firestore commit. Changes are
  // accumulated per view, so toggling filters across more than one view inside the debounce window
  // is flushed as one batched writeBatch() instead of a separate write per view.
  const pendingByViewRef = useRef<Map<string, Partial<NewTaskFilterInterface>>>(new Map());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushPreferenceWrite = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (pendingByViewRef.current.size === 0) return;
    const updates = Array.from(pendingByViewRef.current.entries())
      .filter(([, changes]) => Object.keys(changes).length > 0)
      .map(([viewId, changes]) => ({ itemType: 'views', itemId: viewId, data: changes }));
    pendingByViewRef.current = new Map();
    if (updates.length > 0) void batchUpdatePreferences(updates);
  }, []);

  const schedulePreferenceWrite = useCallback(
    (viewId: string, changes: Partial<NewTaskFilterInterface>) => {
      const existing = pendingByViewRef.current.get(viewId) || {};
      pendingByViewRef.current.set(viewId, { ...existing, ...changes });
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(flushPreferenceWrite, PREFERENCE_WRITE_DEBOUNCE_MS);
    },
    [flushPreferenceWrite]
  );

  // Flush any pending preference write when the dropdown unmounts so the last change isn't lost.
  useEffect(() => flushPreferenceWrite, [flushPreferenceWrite]);

  const groupMenuItems = [
    { key: SHOW_TASK_GROUPING_TYPE.NO_GROUPING, label: 'No Grouping' },
    { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_PRIORITY, label: 'Priority' },
    { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_USER, label: 'User' },
    { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS, label: 'Status' },
    { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_PROJECT, label: 'Project' }
  ];

  const handleTaskListTypeChange = useCallback(
    async (type: number) => {
      if (createViewInput?.id) {
        createViewInput.updateTaskFilterType('taskViewType', type);
      } else {
        if (currentView) {
          currentView.updateTaskFilterType('taskViewType', type);
          schedulePreferenceWrite(currentView.id, { taskViewType: type });
        }
      }
    },
    [createViewInput, currentView, schedulePreferenceWrite]
  );

  const handleShowRecentlyCompletedTaskChange = useCallback(
    async (value: boolean) => {
      if (createViewInput?.id) {
        createViewInput.updateTaskFilterType('showRecentlyCompletedTask', value);
      } else {
        if (currentView) {
          currentView.updateTaskFilterType('showRecentlyCompletedTask', value);
          schedulePreferenceWrite(currentView.id, { showRecentlyCompletedTask: value });
        }
      }
    },
    [createViewInput, currentView, schedulePreferenceWrite]
  );

  const handleShowCompletedTaskByFilterChange = useCallback(
    async (value: number) => {
      if (createViewInput?.id) {
        createViewInput.updateTaskFilterType('showCompletedTaskByFilter', value);
      } else {
        if (currentView) {
          currentView.updateTaskFilterType('showCompletedTaskByFilter', value);
          schedulePreferenceWrite(currentView.id, { showCompletedTaskByFilter: value });
        }
      }
      setIsOpenCompletedTaskDropdown(false);
    },
    [createViewInput, currentView, schedulePreferenceWrite]
  );

  const handleGroupingChange = useCallback(
    async (value: number) => {
      if (createViewInput?.id) {
        createViewInput.updateTaskFilterType('groupingStatus', value);
      } else {
        if (currentView) {
          currentView.updateTaskFilterType('groupingStatus', value);
          schedulePreferenceWrite(currentView.id, { groupingStatus: value });
        }
      }
      setIsOpenGroupMenu(false);
    },
    [createViewInput, currentView, schedulePreferenceWrite]
  );

  const handleOrderingChange = useCallback(
    async (value: number) => {
      if (createViewInput?.id) {
        createViewInput.updateTaskFilterType('orderByStatus', value);
      } else {
        if (currentView) {
          currentView.updateTaskFilterType('orderByStatus', value);
          schedulePreferenceWrite(currentView.id, { orderByStatus: value });
        }
      }
      setIsOrderDropdownOpen(false);
    },
    [createViewInput, currentView, schedulePreferenceWrite]
  );

  const handleShowSubTaskChange = useCallback(
    async (value: boolean) => {
      if (createViewInput?.id) {
        createViewInput.updateTaskFilterType('showSubTask', value);
      } else {
        if (currentView) {
          currentView.updateTaskFilterType('showSubTask', value);
          schedulePreferenceWrite(currentView.id, { showSubTask: value });
        }
      }
    },
    [createViewInput, currentView, schedulePreferenceWrite]
  );

  const handleShowEmptyGroupChange = useCallback(
    async (value: boolean) => {
      if (createViewInput?.id) {
        createViewInput.updateTaskFilterType('showEmptyGroup', value);
      } else {
        if (currentView) {
          currentView.updateTaskFilterType('showEmptyGroup', value);
          schedulePreferenceWrite(currentView.id, { showEmptyGroup: value });
        }
      }
    },
    [createViewInput, currentView, schedulePreferenceWrite]
  );

  const onGroupButtonClick = useCallback(() => {
    setIsOpenGroupMenu(!isOpenGroupMenu);
  }, [isOpenGroupMenu]);

  const onButtonClickOrdering = useCallback(() => {
    setIsOrderDropdownOpen(!isOrderDropdownOpen);
  }, [isOrderDropdownOpen]);

  const onTaskButtonClick = useCallback(() => {
    setIsOpenCompletedTaskDropdown(!isOpenCompletedTaskDropdown);
  }, [isOpenCompletedTaskDropdown]);

  // render view Filter Tabs
  const renderTabButton = useCallback(() => {
    return viewFilterTaskOption?.map((tab) => {
      return (
        <Tabs
          key={tab?.key}
          variant={TAB_BUTTON_TYPE.STANDARD}
          size='small'
          title={tab?.title}
          onClick={() => handleTaskListTypeChange(tab?.key)}
          isActive={displayFilters.taskViewType === tab?.key}
          iconName={tab?.iconName}
          iconPosition='start'
          applyFullWidth={true}
        />
      );
    });
  }, [displayFilters.taskViewType, handleTaskListTypeChange]);

  const renderTaskFilterDropdown = () => (
    <TaskDropdown>
      <TaskDrop isTaskList>
        <Data>
          <Options>{renderTabButton()}</Options>
        </Data>
        <TaskFilterDiv>
          <Data>
            <GroupDiv>
              <SVGDiv>
                <SVGIcon
                  name='updated-group-icon'
                  width={ICON_SIZE.SM}
                  height={ICON_SIZE.SM}
                  viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                  fill='var(--text-secondary)'
                />
              </SVGDiv>
              <DropText>Grouping</DropText>
            </GroupDiv>
            <Dropdown
              modalTitle='Grouping'
              content={
                <Chip
                  variant={CHIP_TYPE.FILL}
                  size={CHIP_SIZE.SMALL}
                  title={SHOW_TASK_GROUPING_NAME[displayFilters.groupingStatus || 1]}
                  onClick={onGroupButtonClick}
                  isActive={isOpenGroupMenu}
                  trailingIconName='down-arrow-icon'
                  trailingIconClassName={isOpenGroupMenu ? 'dropdown-icon' : ''}
                  iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                />
              }
              trigger='click'
              onOutsideClick={() => setIsOpenGroupMenu(false)}>
              {groupMenuItems.map(({ key, label }) => (
                <DropdownItem
                  key={key}
                  label={label}
                  onClick={() => handleGroupingChange(Number(key))}
                  isActive={displayFilters.groupingStatus === Number(key)}
                />
              ))}
            </Dropdown>
          </Data>
          <Data>
            <GroupDiv>
              <SVGDiv>
                <SVGIcon
                  name='small-filters-icon'
                  width={ICON_SIZE.SM}
                  height={ICON_SIZE.SM}
                  viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                  className='filters-icon-color'
                />
              </SVGDiv>
              <DropText>Ordering</DropText>
            </GroupDiv>
            <Dropdown
              modalTitle='Ordering'
              content={
                <Chip
                  variant={CHIP_TYPE.FILL}
                  size={CHIP_SIZE.SMALL}
                  title={SHOW_TASK_ORDER_NAME[displayFilters.orderByStatus || SHOW_TASK_BY_ORDER.CUSTOM]}
                  onClick={onButtonClickOrdering}
                  isActive={isOrderDropdownOpen}
                  trailingIconName='down-arrow-icon'
                  trailingIconClassName={isOrderDropdownOpen ? 'dropdown-icon' : ''}
                  iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                />
              }
              trigger='click'
              onOutsideClick={() => setIsOrderDropdownOpen(false)}>
              {Object.entries(SHOW_TASK_ORDER_NAME).map(([key, label]) => (
                <DropdownItem
                  key={key}
                  label={label}
                  onClick={() => handleOrderingChange(Number(key))}
                  isActive={displayFilters.orderByStatus === Number(key)}
                />
              ))}
            </Dropdown>
          </Data>
        </TaskFilterDiv>
      </TaskDrop>
      <TaskFilterDiv>
        {displayFilters.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS && (
          <TaskDrop isTaskList>
            <Data>
              <GroupDiv>
                <DropText>Task completed by recency</DropText>
              </GroupDiv>
              <SwitchInput
                checked={displayFilters.showRecentlyCompletedTask}
                onChange={(e) => handleShowRecentlyCompletedTaskChange(e.target.checked)}
              />
            </Data>
            {displayFilters.taskViewType !== TASK_LIST_TYPE.CALENDAR_VIEW && (
              <Data>
                <GroupDiv>
                  <DropText>Completed tasks</DropText>
                </GroupDiv>
                <Dropdown
                  modalTitle='Completed tasks'
                  content={
                    <Chip
                      variant={CHIP_TYPE.FILL}
                      size={CHIP_SIZE.SMALL}
                      title={
                        SHOW_COMPLETED_TASK_FILTER_NAME[
                          displayFilters.showCompletedTaskByFilter || SHOW_COMPLETED_TASK_BY_DATE.LAST_MONTH
                        ]
                      }
                      onClick={onTaskButtonClick}
                      isActive={isOpenCompletedTaskDropdown}
                      trailingIconName='down-arrow-icon'
                      trailingIconClassName={isOpenCompletedTaskDropdown ? 'dropdown-icon' : ''}
                      iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                    />
                  }
                  trigger='click'
                  onOutsideClick={() => setIsOpenCompletedTaskDropdown(false)}>
                  {Object.entries(SHOW_COMPLETED_TASK_FILTER_NAME).map(([key, label]) => (
                    <DropdownItem
                      key={key}
                      label={label}
                      onClick={() => handleShowCompletedTaskByFilterChange(Number(key))}
                      isActive={displayFilters.showCompletedTaskByFilter === Number(key)}
                    />
                  ))}
                </Dropdown>
              </Data>
            )}
          </TaskDrop>
        )}
        <TaskDrop>
          <Data>
            <GroupDiv>
              <DropText>Show sub-tasks</DropText>
            </GroupDiv>
            <SwitchInput
              checked={displayFilters.showSubTask}
              onChange={(e) => handleShowSubTaskChange(e.target.checked)}
            />
          </Data>
          {displayFilters.taskViewType !== TASK_LIST_TYPE.CALENDAR_VIEW && (
            <Data>
              <GroupDiv>
                <DropText>Show empty groups</DropText>
              </GroupDiv>
              <SwitchInput
                checked={displayFilters.showEmptyGroup}
                onChange={(e) => handleShowEmptyGroupChange(e.target.checked)}
              />
            </Data>
          )}
        </TaskDrop>
      </TaskFilterDiv>
    </TaskDropdown>
  );

  const renderDisplayButton = () => (
    <Chip
      title='Display'
      size={CHIP_SIZE.SMALL}
      variant={CHIP_TYPE.FILL}
      leadingIconName='filter-icon'
      textColorType='secondary'
      isActive={taskStore.isTaskFilterOpen}
      onClick={() => taskStore.setIsTaskFilterOpen(!taskStore.isTaskFilterOpen)}
    />
  );

  return (
    <>
      {isMobile &&
        (isCreateView ? (
          renderDisplayButton()
        ) : (
          <IconButton
            size={ICON_BUTTON_SIZE.LARGE}
            variant={ICON_BUTTON_TYPE.TERTIARY}
            iconName='filter-icon'
            onClick={() => taskStore.setIsTaskFilterOpen(!taskStore.isTaskFilterOpen)}
          />
        ))}
      {!isMobile && (
        <TaskFilterCustomDropdown
          isMinWidth={289}
          trigger={renderDisplayButton}
          content={renderTaskFilterDropdown()}
          isTaskFilterOpen={isTaskFilterOpen}
        />
      )}
      {isMobile && (
        <Modal open={isTaskFilterOpen} onClose={() => taskStore.setIsTaskFilterOpen(false)} maxWidth={'362px'}>
          <TaskFilterCustomDropdown
            isMinWidth={289}
            content={renderTaskFilterDropdown()}
            isTaskFilterOpen={isTaskFilterOpen}
            isModal={true}
          />
        </Modal>
      )}
    </>
  );
};

export default observer(DisplayFilterDropdown);
